import React, { createContext, useContext, useState, useEffect } from 'react';
import { SongService } from '../services/SongService';
import { AdminService } from '../services/AdminService';
import { DistributionService } from '../services/DistributionService';
import { SocialService } from '../services/SocialService';
import { VirtualCurrencyService } from '../services/VirtualCurrencyService';
import { SongLibraryService } from '../services/SongLibraryService';
import { ReferralService } from '../services/ReferralService';
import { useUser } from './UserContext';
import { supabase } from '../lib/supabase';

interface Profile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
}

interface DataContextType {
  songService: SongService;
  adminService: AdminService;
  distributionService: DistributionService;
  socialService: SocialService;
  virtualCurrencyService: VirtualCurrencyService;
  songLibraryService: SongLibraryService;
  referralService: ReferralService;
  profile: Profile | null;
  loading: boolean;
  error: Error | null;
  refreshProfile: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const useProfile = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useProfile must be used within a DataProvider');
  }
  return {
    profile: context.profile,
    loading: context.loading,
    error: context.error
  };
};

// UUID validation regex pattern
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const songService = new SongService();
  const adminService = new AdminService();
  const distributionService = new DistributionService();
  const socialService = new SocialService();
  const virtualCurrencyService = new VirtualCurrencyService();
  const songLibraryService = new SongLibraryService();
  const referralService = new ReferralService();
  const { user } = useUser();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refreshProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, username, avatar_url')
      .eq('id', user.id)
      .maybeSingle();
    if (data) setProfile(data);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    async function loadProfile() {
      try {
        // Clear profile if no user
        if (!user) {
          setProfile(null);
          setLoading(false);
          return;
        }

        // Handle missing or invalid user ID
        if (!user.id || !UUID_PATTERN.test(user.id)) {
          console.warn('Invalid or missing user ID, skipping profile load.');
          setProfile(null);
          setLoading(false);
          return;
        }

        console.log('Loading profile for user:', user.id);

        // Try to get the existing profile - use maybeSingle() instead of single()
        const { data: existingProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url')
          .eq('id', user.id)
          .maybeSingle();

        if (fetchError) {
          console.error('Error fetching profile:', fetchError);
          throw fetchError;
        }

        // If profile exists, use it
        if (existingProfile) {
          console.log('Found existing profile:', existingProfile);
          setProfile(existingProfile);
          setLoading(false);
          return;
        }

        console.log('No profile found, creating new profile');

        // Generate a default username from email if available
        let defaultUsername = user.email 
          ? user.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '')
          : `user_${Math.random().toString(36).substring(2, 8)}`;

        // Check if username already exists and make it unique if needed
        const { data: existingUsernames, error: usernameError } = await supabase
          .from('profiles')
          .select('username')
          .eq('username', defaultUsername)
          .maybeSingle();

        if (usernameError) {
          console.warn('Error checking username uniqueness:', usernameError);
        }

        // Make username unique if it already exists
        if (existingUsernames) {
          defaultUsername = `${defaultUsername}_${Math.random().toString(36).substring(2, 6)}`;
          console.log('Generated unique username:', defaultUsername);
        }

        // Create a new profile - don't use any ON CONFLICT clauses
        try {
          // Direct insert without upsert
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              full_name: user.user_metadata?.full_name || null,
              username: defaultUsername,
              avatar_url: null
            })
            .select();

          if (insertError) {
            console.error('Error inserting profile:', insertError);
            throw insertError;
          }

          // If insert succeeds but no data returned, try to fetch the profile
          if (!newProfile || newProfile.length === 0) {
            console.log('Profile created but no data returned, fetching profile');
            
            // Add a small delay to allow the database to complete the insert
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            const { data: createdProfile, error: fetchCreatedError } = await supabase
              .from('profiles')
              .select('id, full_name, username, avatar_url')
              .eq('id', user.id)
              .maybeSingle();
              
            if (fetchCreatedError) {
              console.error('Error fetching created profile:', fetchCreatedError);
              throw fetchCreatedError;
            }
              
            if (createdProfile) {
              console.log('Retrieved created profile:', createdProfile);
              setProfile(createdProfile);
              setLoading(false);
              return;
            } else {
              throw new Error('Profile not found after creation');
            }
          } else {
            console.log('Created new profile:', newProfile[0]);
            setProfile(newProfile[0]);
            setLoading(false);
            return;
          }
        } catch (insertError) {
          console.error('Error creating profile:', insertError);
          
          // Check if error is about duplicate key - the profile might already exist
          const errorMessage = String(insertError);
          if (errorMessage.includes('duplicate key') || errorMessage.includes('unique constraint')) {
            console.log('Profile may already exist despite earlier check, trying to fetch again');
            
            // Try one more time to get the profile
            const { data: retryProfile, error: retryError } = await supabase
              .from('profiles')
              .select('id, full_name, username, avatar_url')
              .eq('id', user.id)
              .maybeSingle();

            if (retryError) {
              console.error('Error on retry profile fetch:', retryError);
              throw retryError;
            }

            if (retryProfile) {
              console.log('Found profile on retry:', retryProfile);
              setProfile(retryProfile);
              setLoading(false);
              return;
            }
          }
          
          // If it still fails, set a default profile to prevent cascading failures
          console.warn('Unable to create or fetch profile, using fallback profile');
          if (user?.id) {
            const fallbackProfile = {
              id: user.id,
              full_name: user.user_metadata?.full_name || null,
              username: defaultUsername,
              avatar_url: null
            };
            setProfile(fallbackProfile);
            setLoading(false);
            return;
          } else {
            throw new Error('Unable to create profile and no user ID for fallback');
          }
        }
      } catch (err) {
        console.error('Profile loading error:', err);
        setError(err instanceof Error ? err : new Error('Failed to load profile'));
        
        // Set default profile on error to prevent cascading failures
        if (user?.id) {
          setProfile({
            id: user.id,
            full_name: null,
            username: null,
            avatar_url: null
          });
        } else {
          setProfile(null);
        }
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user]);

  return (
    <DataContext.Provider value={{
      songService,
      adminService,
      distributionService,
      socialService,
      virtualCurrencyService,
      songLibraryService,
      referralService,
      profile,
      loading,
      error,
      refreshProfile,
    }}>
      {children}
    </DataContext.Provider>
  );
};